<template>
  <div class="pcPanel">
    <div class="pcPanel__title">
      {{
        $t("app.home.map_store_query_title") +
        (!innerLocationPermission
          ? $t("app.home.map_store_location_fallback_hint")
          : "")
      }}
    </div>

    <div class="pcPanel__filters">
      <!-- 不用 DropdownMenu：Popup 在浮层内易整块居中；自定义触发器 + overlay + 固定在城市栏下方 -->
      <div class="pcPanel__city" ref="cityTriggerRef">
        <div
          class="pcPanel__cityTrigger"
          role="button"
          tabindex="0"
          @click="openCityList"
          @keydown.enter.prevent="openCityList"
        >
          <span class="pcPanel__cityTriggerText">{{ currentCityLabel }}</span>
          <span
            class="pcPanel__cityArrow"
            :class="{ 'pcPanel__cityArrow--up': showCityList }"
          />
        </div>
      </div>
      <teleport to="body">
        <van-overlay
          :show="showCityList"
          :z-index="3998"
          @click="showCityList = false"
        />
        <div
          v-show="showCityList"
          class="mapStorePcCityPanel"
          :style="cityPanelStyle"
          @click.stop
        >
          <div class="mapStorePcCityPanel__search">
            <div class="mapStorePcCityPanel__searchWrap">
              <input
                v-model.trim="citySearchKeyword"
                type="text"
                class="mapStorePcCityPanel__input"
                :placeholder="
                  $t('app.home.map_store_search_province_placeholder')
                "
              />
              <van-icon
                v-show="hasCitySearchKeyword"
                name="cross"
                class="mapStorePcCityPanel__searchClear"
                @click.stop="citySearchKeyword = ''"
              />
            </div>
          </div>
          <div class="mapStorePcCityPanel__list">
            <div
              v-for="opt in filteredCityOptions"
              :key="String(opt.value)"
              class="mapStorePcCityOption"
              :class="{
                'mapStorePcCityOption--active': opt.value === innerCity,
              }"
              @click="selectCity(opt)"
            >
              {{ opt.text }}
            </div>
          </div>
        </div>
      </teleport>

      <!--
        Vant Field 的 clear 只在 clear 图标上监听了 touchstart，PC 点击无效；
        关闭 clearable，用 #button 插槽自绘叉号并 @click 清空。
      -->
      <van-field
        class="pcPanel__search"
        v-model.trim="innerKeyword"
        :clearable="false"
        :placeholder="$t('app.home.map_store_search_store_or_code_placeholder')"
      >
        <template #left-icon>
          <span class="pcPanel__searchIconWrap">
            <img class="pcPanel__searchIcon" :src="iconSearch" alt="" />
          </span>
        </template>
        <template #button>
          <van-icon
            v-show="innerKeyword && String(innerKeyword).length > 0"
            name="cross"
            class="pcPanel__searchClear"
            @click.stop="onClearKeyword"
          />
        </template>
      </van-field>
    </div>

    <!-- PC 端无数据：居中显示与手机端相同文案 -->
    <div v-if="list.length === 0" class="pcPanel__empty">
      <p class="pcPanel__emptyLine1">
        {{ $t("app.home.map_store_empty_area_line1") }}
      </p>
      <p class="pcPanel__emptyLine2">
        {{ $t("app.home.map_store_empty_area_line2") }}
      </p>
    </div>

    <div v-else class="pcPanel__list">
      <div
        v-for="(item, idx) in list"
        :key="item.id ?? idx"
        class="store"
        :class="{ 'store--active': (item.id ?? idx) === activeKey }"
        @click="onSelect(item, idx)"
      >
        <div class="store__head">
          <div class="store__name">{{ item.fullName || item.name || "-" }}</div>
          <div class="store__right" v-if="item.showdistancekm">
            <img class="store__navicon" :src="iconNav" alt="" />
            <div class="store__distance">
              {{ item.distancekm + "km" }}
            </div>
          </div>
        </div>

        <div class="store__main">
          <div class="store__rows">
            <div class="store__row" v-if="item.address">
              <img class="store__row__location" :src="iconLoc" alt="" />
              <div class="store__row__text">{{ item.address }}</div>
            </div>
          </div>

          <button
            v-if="item.distancekm && item.showdistancekm"
            class="store__navbtn"
            @click.stop="onNavigate(item, idx)"
          >
            {{ $t("app.home.map_store_nav_button") }}
          </button>
        </div>
      </div>
    </div>
  </div>
</template>

<script>
import { computed, ref, watch, onMounted, onUnmounted } from "vue";
import { useI18n } from "vue-i18n";
import { useRoute, useRouter } from "vue-router";
import {
  CHINA_ALL_PROVINCE,
  CHINA_DEFAULT_PROVINCE_ID,
  getAllProvinceList,
} from "@/utils/config";
import iconNav from "@/assets/map-icons/navigation.png";
import iconLoc from "@/assets/map-icons/map_location.png";
import iconSearch from "@/assets/map-icons/map_search.png";

const FAKE_DISTANCE_KM = 2.1;

const extractCity = (address) => {
  if (!address) return "";
  const m = String(address).match(/([^省市区县]+市)/);
  return m ? m[1] : "";
};

export default {
  name: "MapStorePcPanel",
  props: {
    dealers: { type: Array, default: () => [] },
    keyword: { type: String, default: "" },
    city: { type: String, default: "ALL" },
    activeKey: { type: [String, Number], default: null },
    currentLocation: { type: Object, default: () => ({}) },
    locationPermission: { type: Boolean, default: false },
  },
  emits: [
    "update:keyword",
    "update:city",
    "update:currentLocation",
    "select",
    "navigate",
  ],
  setup(props, { emit }) {
    const route = useRoute();
    const { t } = useI18n();
    const innerKeyword = ref(props.keyword);
    const innerCity = ref(props.city || CHINA_DEFAULT_PROVINCE_ID);
    const innerLocationPermission = ref(props.locationPermission);
    // 与手机端一致：省份选项来自 CHINA_ALL_PROVINCE（value 为省份 ID）
    const cityOptions = ref(CHINA_ALL_PROVINCE);

    watch(
      () => props.keyword,
      (v) => (innerKeyword.value = v)
    );
    watch(
      () => props.city,
      (v) => (innerCity.value = v || CHINA_DEFAULT_PROVINCE_ID)
    );
    watch(
      () => props.currentLocation,
      (v) => emit("update:currentLocation", v || {}),
      { deep: true }
    );

    watch(
      () => props.locationPermission,
      (v) => {
        innerLocationPermission.value = v;
      }
    );

    watch(
      () => route.query.lang,
      (lang) => {
        if (lang == "en") {
          cityOptions.value = getAllProvinceList();
        }
        if (lang == "zh") {
          cityOptions.value = getAllProvinceList();
        }
      },
      { immediate: true }
    );

    watch(innerKeyword, (v) => emit("update:keyword", v));
    /** 点击清空图标时与 v-model.trim 组合偶发不置空，显式同步 */
    const onClearKeyword = () => {
      innerKeyword.value = "";
      emit("update:keyword", "");
    };
    watch(innerCity, (v) =>
      emit("update:city", v || CHINA_DEFAULT_PROVINCE_ID)
    );

    const sourceDealers = computed(() => {
      const real = props.dealers || [];
      return real;
    });

    const list = computed(() => {
      const kw = (props.keyword || "").trim().toLowerCase();
      return (sourceDealers.value || []).filter((d) => {
        const hay = `${d.fullName || ""} ${d.id || ""} ${
          d.code || ""
        }`.toLowerCase();
        const inKw = kw ? hay.includes(kw) : true;
        return inKw;
      });
    });

    const showCityList = ref(false);
    const cityTriggerRef = ref(null);
    const cityPanelStyle = ref({});

    const currentCityLabel = computed(() => {
      const opts = cityOptions.value || [];
      const o = opts.find((x) => x.value === innerCity.value);
      return o ? o.text : t("app.home.map_store_all_provinces");
    });

    const citySearchKeyword = ref("");
    const hasCitySearchKeyword = computed(
      () => (citySearchKeyword.value || "").trim().length > 0
    );
    /** 城市下拉内根据输入模糊匹配的选项 */
    const filteredCityOptions = computed(() => {
      const opts = cityOptions.value || [];
      const kw = (citySearchKeyword.value || "").trim().toLowerCase();
      if (!kw) return opts;
      return opts.filter((o) => (o.text || "").toLowerCase().includes(kw));
    });

    const updateCityPanelPosition = () => {
      const el = cityTriggerRef.value;
      if (!el || !showCityList.value) return;
      const rect = el.getBoundingClientRect();
      const minPanelWidth = 260;
      const panelWidth = Math.max(rect.width, minPanelWidth);
      cityPanelStyle.value = {
        top: `${rect.bottom + 4}px`,
        left: `${rect.left}px`,
        width: `${panelWidth}px`,
        zIndex: 4000,
      };
    };

    const openCityList = () => {
      showCityList.value = !showCityList.value;
      if (showCityList.value) {
        citySearchKeyword.value = "";
        requestAnimationFrame(() => updateCityPanelPosition());
      }
    };

    const selectCity = (opt) => {
      innerCity.value = opt.value;
      showCityList.value = false;
    };

    const onResizeOrScroll = () => {
      if (showCityList.value) updateCityPanelPosition();
    };

    onMounted(() => {
      window.addEventListener("resize", onResizeOrScroll);
      window.addEventListener("scroll", onResizeOrScroll, true);
    });
    onUnmounted(() => {
      window.removeEventListener("resize", onResizeOrScroll);
      window.removeEventListener("scroll", onResizeOrScroll, true);
    });

    const onSelect = (item, idx) =>
      emit("select", { item, idx, key: item.id ?? idx });
    const onNavigate = (item, idx) =>
      emit("navigate", { item, idx, key: item.id ?? idx });

    return {
      innerKeyword,
      onClearKeyword,
      innerCity,
      cityOptions,
      citySearchKeyword,
      hasCitySearchKeyword,
      filteredCityOptions,
      showCityList,
      cityTriggerRef,
      cityPanelStyle,
      currentCityLabel,
      openCityList,
      selectCity,
      list,
      onSelect,
      onNavigate,
      iconNav,
      iconLoc,
      iconSearch,
      innerLocationPermission,
      t,
    };
  },
};
</script>

<style lang="scss" scoped>
.pcPanel {
  height: 100%;
  max-height: 100%;
  display: flex;
  flex-direction: column;
  background: #fff;
  border-radius: 0.12rem;
  overflow: hidden;
  box-shadow: 0 0.06rem 0.24rem rgba(0, 0, 0, 0.12);

  &__title {
    padding: 0.16rem;
    font-size: 0.14rem;
    font-weight: 700;
    color: #383838;
    border-bottom: 1px solid #f2f2f2;
  }

  &__filters {
    padding: 0.12rem 0.16rem;
    display: flex;
    gap: 0.1rem;
    align-items: center;
  }
  &__city {
    width: 1.4rem;
    height: 36px;
    flex-shrink: 0;
  }
  /* 城市选择：与设计图一致，浅灰细边框 + 圆角，与搜索框同高同风格，右侧小箭头 */
  &__cityTrigger {
    display: flex;
    align-items: center;
    justify-content: space-between;
    width: 100%;
    height: 0.38rem;
    padding: 0 0.06rem 0 0.08rem;
    border: 1px solid #e7e7e7;
    border-radius: 0.08rem;
    background: #fff;
    cursor: pointer;
    font-size: 0.14rem;
    color: #323233;
    box-sizing: border-box;
  }
  &__cityTriggerText {
    overflow: hidden;
    text-overflow: ellipsis;
    white-space: nowrap;
    flex: 1;
    min-width: 0;
    text-align: left;
  }
  &__cityArrow {
    flex-shrink: 0;
    display: block;
    width: 0.08rem;
    height: 0.06rem;
    margin-left: 0.04rem;
    background: url("~@/assets/map-icons/map_down.png") no-repeat center /
      contain;
    opacity: 0.7;
    &--up {
      background-image: url("~@/assets/map-icons/map_up.png");
    }
  }
  &__search {
    flex: 1;
    height: 36px;
    border: 1px solid #e7e7e7;
    border-radius: 0.08rem;
    padding: 0.02rem 0.08rem;
  }
  &__searchIconWrap {
    display: inline-flex;
    align-items: center;
    justify-content: center;
    height: 100%;
  }
  &__searchIcon {
    display: block;
    width: 0.18rem;
    height: 0.18rem;
    object-fit: contain;
  }

  &__list {
    flex: 1;
    overflow: auto;
  }
}

/* PC 空数据文案：垂直水平居中，文案与手机端一致 */
.pcPanel__empty {
  flex: 1;
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
  padding: 0.16rem;
  text-align: center;
}
.pcPanel__emptyLine1,
.pcPanel__emptyLine2 {
  margin: 0;
  line-height: 1.6;
}
.pcPanel__emptyLine1 {
  font-size: 0.14rem;
  color: #383838;
}
.pcPanel__emptyLine2 {
  font-size: 0.13rem;
  color: #707070;
  margin-top: 0.04rem;
}

/* teleport 到 body，需非 scoped 或 :global，否则列表无样式 */

.store {
  position: relative;
  padding: 0.16rem;
  background: #fff;
  border-bottom: 1px solid #ebebeb;

  &--active {
    background: rgba(0, 147, 67, 0.12);
  }
  &--active::before {
    content: "";
    position: absolute;
    left: 0;
    top: 0;
    bottom: 0;
    width: 0.05rem;
    background: #009343;
  }
  &--active &__name {
    color: #009343;
  }

  &__head {
    display: flex;
    align-items: center;
    gap: 0.1rem;
  }
  &__name {
    flex: 1;
    font-size: 0.14rem;
    font-weight: 700;
    color: #383838;
    line-height: 1.2;
  }
  &__right {
    display: flex;
    align-items: center;
    gap: 0.04rem;
    color: #8a8a8a;
    font-size: 0.13rem;
  }
  &__navicon {
    width: 0.1rem;
    height: 0.1rem;
    opacity: 0.85;
  }
  &__distance {
    color: #707070;
    font-size: 0.12rem;
  }
  &__badge {
    display: inline-block;
    margin-top: 0.08rem;
    padding: 0.04rem 0.12rem;
    border-radius: 0.12rem;
    border: 1px solid #009343;
    color: #009343;
    font-size: 0.1rem;
    background: rgba(0, 147, 67, 0.06);
  }
  &__main {
    margin-top: 0.08rem;
    display: flex;
    gap: 0.12rem;
    align-items: flex-end;
  }
  &__rows {
    flex: 1;
    min-width: 0;
  }
  &__row {
    display: flex;
    align-items: flex-start;
    gap: 0.08rem;
    margin-top: 0.08rem;
    &:first-child {
      margin-top: 0;
    }
    &__imgicon {
      width: 0.12rem;
      height: 0.12rem;
      margin-top: 0.02rem;
      opacity: 0.85;
    }
    &__location {
      width: 0.1rem;
      height: 0.12rem;
      margin-top: 0.02rem;
      opacity: 0.85;
    }
    &__text {
      flex: 1;
      line-height: 1.25;
      word-break: break-all;
      font-size: 0.12rem;
      color: #383838;
    }
  }
  &__navbtn {
    flex: 0 0 auto;
    padding: 0.06rem 0.16rem;
    border-radius: 0.2rem;
    border: 0.01rem solid #cccccc;
    background: #fff;
    color: #707070;
    font-size: 0.13rem;
  }
  &--active &__navbtn {
    border-color: #009343;
    color: #009343;
    background: rgba(0, 147, 67, 0.06);
  }
}

/* 搜索图标/文案垂直对齐 */
::v-deep(.pcPanel__search .van-field__body) {
  align-items: center;
}
::v-deep(.pcPanel__search .van-field__left-icon) {
  display: flex;
  align-items: center;
  height: 100%;
}
::v-deep(.pcPanel__search .van-field__control) {
  line-height: 1.2;
  padding: 0;
}
/* 自定义清空按钮：可点区域与反馈（Vant 自带 clear 仅 touchstart，PC 无效） */
::v-deep(.pcPanel__search .van-field__button) {
  display: flex;
  align-items: center;
  padding-right: 0.02rem;
}
.pcPanel__searchClear {
  font-size: 0.16rem;
  color: #c8c9cc;
  cursor: pointer;
  padding: 0.04rem;
}
.pcPanel__searchClear:active {
  opacity: 0.7;
}
::v-deep(.van-cell__value) {
  display: flex;
  align-items: center;
  justify-content: left;
}

::v-deep(.van-field__control) {
  font-size: 0.14rem;
}
</style>

<!-- teleport 到 body，无 scoped 才能生效 -->
<style lang="scss">
.mapStorePcCityPanel {
  position: fixed;
  max-height: 50vh;
  display: flex;
  flex-direction: column;
  overflow: hidden;
  background: #fff;
  border-radius: 0.08rem;
  box-shadow: 0 0.06rem 0.2rem rgba(0, 0, 0, 0.15);
  border: 1px solid #ebedf0;
}
/* 城市搜索框：边框，有输入时右侧显示清除图标 */
.mapStorePcCityPanel__search {
  flex-shrink: 0;
  padding: 0.08rem 0.1rem;
  border-bottom: 1px solid #f2f2f2;
}
.mapStorePcCityPanel__searchWrap {
  display: flex;
  align-items: center;
  border: 1px solid #e7e7e7;
  border-radius: 0.08rem;
  background: #fff;
  padding: 0 0.08rem 0 0;
  box-sizing: border-box;
}
.mapStorePcCityPanel__input {
  flex: 1;
  min-width: 0;
  height: 0.38rem;
  padding: 0.02rem 0.08rem 0.02rem 0.1rem;
  font-size: 0.14rem;
  border: none;
  border-radius: 0;
  outline: none;
  line-height: 1.2;
  background: transparent;
}
.mapStorePcCityPanel__input::placeholder {
  color: #c8c9cc;
}
.mapStorePcCityPanel__searchClear {
  flex-shrink: 0;
  margin-left: 0.04rem;
  font-size: 0.16rem;
  color: #c8c9cc;
  cursor: pointer;
  padding: 0.04rem;
}
.mapStorePcCityPanel__searchClear:hover {
  opacity: 0.8;
}
.mapStorePcCityPanel__list {
  flex: 1;
  min-height: 0;
  overflow-y: auto;
}
.mapStorePcCityOption {
  padding: 0.12rem 0.14rem;
  font-size: 0.14rem;
  color: #323233;
  cursor: pointer;
  border-bottom: 1px solid #f2f2f2;
}
.mapStorePcCityOption:last-child {
  border-bottom: none;
}
.mapStorePcCityOption:hover {
  background: #f7f8fa;
}
.mapStorePcCityOption--active {
  color: #009343;
  font-weight: 600;
  background: rgba(0, 147, 67, 0.08);
}
</style>

